import { WebViewerInstance } from '@pdftron/webviewer';
import PDFManagerFactory from 'pdftron/PDFManagerFactory';
import { DocumentTypes } from 'types';
import {
  RenderSettings,
  getRenderSettings,
  getStockCanvasMultiplier,
  getStockRenderSettings,
  settingsEqual,
} from 'zstore/renderSettingsStore';

const DOCUMENT_TYPES = [DocumentTypes.source, DocumentTypes.target] as const;

type WebViewerCore = WebViewerInstance['Core'];

export function setRenderSettingsOnCore(core: WebViewerCore, settings: RenderSettings): void {
  const { setCachingLevel, setPreRenderLevel, setProgressiveTime, setCanvasMultiplier, unsetCanvasMultiplier } = core;

  setCachingLevel(settings.cachingLevel);
  setPreRenderLevel(settings.preRenderLevel);
  setProgressiveTime(settings.progressiveTime);

  // Calling setCanvasMultiplier pins the multiplier for good, replacing PDFTron's own
  // devicePixelRatio-driven default. When the requested value is just the stock one we want that
  // default behavior back instead, so "reset to defaults" really resets.
  if (settings.canvasMultiplier === getStockCanvasMultiplier()) {
    unsetCanvasMultiplier();
  } else {
    setCanvasMultiplier(settings.canvasMultiplier);
  }
}

export function applyRenderSettingsToCore(core: WebViewerCore, settings: RenderSettings): void {
  setRenderSettingsOnCore(core, settings);

  core.documentViewer.refreshAll();
  core.documentViewer.updateView();
}

export function applyRenderSettingsToViewers(settings: RenderSettings): void {
  for (const documentType of DOCUMENT_TYPES) {
    const core = PDFManagerFactory.getViewer(documentType)?.instance?.Core;
    if (!core) continue;

    applyRenderSettingsToCore(core, settings);
  }
}

/**
 * Applies the LaunchDarkly render settings at viewer init, before any document is loaded. The
 * setters alone are enough here — there is nothing rendered yet to invalidate, and calling
 * refreshAll/updateView this early throws, which the viewer's init .catch would then report as a
 * failed startup.
 */
export function applyRenderSettingsAtInit(core: WebViewerCore): void {
  const settings = getRenderSettings();
  if (!settingsEqual(settings, getStockRenderSettings())) {
    setRenderSettingsOnCore(core, settings);
  }
}

/** Restores the flag-merged defaults on both viewers. */
export function resetRenderSettingsOnViewers(): void {
  applyRenderSettingsToViewers(getRenderSettings());
}
